import type { ParameterRange, XYZPadAxes } from '@shared/types';

export type XYZPadParseResult = {
  type: 'xyzpad';
  xyzPadAxes: XYZPadAxes;
};

/**
 * Detects XYZ pad syntax for a 3-element numeric vector.
 *
 * Comment pattern: `[<opt-label> min:max, <opt-label> min:max, <opt-label> min:max]`
 *                  or `[min:step:max, min:step:max, min:step:max]`
 *
 * Returns null if the comment does not match the triple-range pattern.
 */
export function parseXYZPadType(
  numElements: number[],
  comment: string,
): XYZPadParseResult | null {
  if (numElements.length !== 3 || !comment) return null;

  const RS =
    '(?:([a-zA-Z][a-zA-Z0-9_]*)\\s+)?(-?\\d+(?:\\.\\d+)?)\\s*(?::\\s*(-?\\d+(?:\\.\\d+)?))?\\s*:\\s*(-?\\d+(?:\\.\\d+)?)';
  const xyzPadPattern = new RegExp(
    `^\\s*\\[\\s*${RS}\\s*,\\s*${RS}\\s*,\\s*${RS}\\s*\\]\\s*$`,
  );
  const xyzMatch = comment.match(xyzPadPattern);
  if (!xyzMatch) return null;

  // Groups per axis (4 groups each): label, first, maybe, last
  // x: 1,2,3,4  y: 5,6,7,8  z: 9,10,11,12
  const parseAxis = (
    label: string | undefined,
    first: string,
    maybe: string | undefined,
    last: string,
  ): { range: ParameterRange; label?: string } => {
    const firstNum = Number(first);
    const lastNum = Number(last);
    if (maybe !== undefined) {
      return {
        range: { min: firstNum, step: Number(maybe), max: lastNum },
        label,
      };
    }
    return { range: { min: firstNum, max: lastNum }, label };
  };

  const xAxis = parseAxis(xyzMatch[1], xyzMatch[2], xyzMatch[3], xyzMatch[4]);
  const yAxis = parseAxis(xyzMatch[5], xyzMatch[6], xyzMatch[7], xyzMatch[8]);
  const zAxis = parseAxis(xyzMatch[9], xyzMatch[10], xyzMatch[11], xyzMatch[12]);

  return {
    type: 'xyzpad',
    xyzPadAxes: {
      x: xAxis.range,
      y: yAxis.range,
      z: zAxis.range,
      xLabel: xAxis.label,
      yLabel: yAxis.label,
      zLabel: zAxis.label,
    },
  };
}
